import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Stack, Typography, Paper, Button, TextField, IconButton,
  CircularProgress, Chip, MenuItem, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  Autocomplete,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { apiGet, apiPost, apiPatch, apiDelete, ApiError } from "../api/client";
import { useToast } from "../ui/Toaster";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const moneyDelta = (n) => {
  const v = Number(n || 0);
  if (v === 0) return money(0);
  return v < 0 ? `−${money(Math.abs(v))}` : money(v);
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function yearStartISO() { return `${new Date().getFullYear()}-01-01`; }

export default function Expenses() {
  const { error: toastError, success: toastSuccess } = useToast();

  const [from, setFrom] = useState(yearStartISO());
  const [to, setTo] = useState(todayISO());
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");        // "" = all
  const [data, setData] = useState({ rows: [], total: 0, by_type: [], all_types: [] });
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null);  // { id?, amount, date, type, notes }
  const [savingDialog, setSavingDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to });
      if (typeFilter) params.set("type", typeFilter);
      if (search.trim()) params.set("q", search.trim());
      const res = await apiGet(`/api/expenses?${params.toString()}`);
      setData(res || { rows: [], total: 0, by_type: [], all_types: [] });
    } catch (e) {
      toastError("Couldn't load expenses", e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [from, to, typeFilter, search, toastError]);

  useEffect(() => { load(); }, [load]);

  const allTypeOptions = useMemo(() => data.all_types || [], [data.all_types]);

  const openNew = () => setEditing({
    amount: "", date: todayISO(), type: "", notes: "",
  });
  const openEdit = (row) => setEditing({
    id: row.id, amount: String(row.amount), date: row.date_iso,
    type: row.type, notes: row.notes,
  });
  const closeDialog = () => setEditing(null);

  const saveDialog = async () => {
    const body = {
      amount: parseFloat(editing.amount),
      date:   editing.date || todayISO(),
      type:   (editing.type || "").trim(),
      notes:  (editing.notes || "").trim(),
    };
    if (!body.type) {
      toastError("Type required", "Pick or type a category");
      return;
    }
    if (!Number.isFinite(body.amount)) {
      toastError("Bad amount", "Enter a number (can be negative for credits)");
      return;
    }
    setSavingDialog(true);
    try {
      if (editing.id) {
        await apiPatch(`/api/expenses/${editing.id}`, body);
        toastSuccess("Updated", `${body.type} — ${money(body.amount)}`);
      } else {
        await apiPost(`/api/expenses`, body);
        toastSuccess("Added", `${body.type} — ${money(body.amount)}`);
      }
      closeDialog();
      load();
    } catch (e) {
      toastError("Save failed", e instanceof ApiError ? e.message : String(e));
    } finally {
      setSavingDialog(false);
    }
  };

  const deleteRow = async (row) => {
    // No popup confirm — too noisy when bulk-editing. The dialog open
    // for the same row already serves as "I meant to touch this row".
    // Operator can re-add if they goof.
    if (!window.confirm(`Delete this ${row.type} expense for ${money(row.amount)}?`)) return;
    try {
      await apiDelete(`/api/expenses/${row.id}`);
      toastSuccess("Deleted", `${row.type} — ${money(row.amount)}`);
      load();
    } catch (e) {
      toastError("Delete failed", e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}
             justifyContent="space-between" alignItems={{ sm: "flex-start" }} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h1" sx={{ fontSize: { xs: "1.85rem", md: "2.5rem" }, mb: 0.5 }}>
            Expenses
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            Operating expenses by category. Feed into the YTD P&L statement.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshOutlinedIcon />} variant="outlined"
                  onClick={load} disabled={loading}>Refresh</Button>
          <Button startIcon={<AddOutlinedIcon />} variant="contained"
                  onClick={openNew}>Add expense</Button>
        </Stack>
      </Stack>

      {/* Filter strip */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
          <TextField label="From" size="small" type="date"
                     value={from} onChange={(e) => setFrom(e.target.value)}
                     InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
          <TextField label="To" size="small" type="date"
                     value={to} onChange={(e) => setTo(e.target.value)}
                     InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
          <TextField label="Type" size="small" select value={typeFilter}
                     onChange={(e) => setTypeFilter(e.target.value)}
                     sx={{ width: 220 }}>
            <MenuItem value="">All types</MenuItem>
            {allTypeOptions.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <TextField label="Search notes / type" size="small"
                     value={search} onChange={(e) => setSearch(e.target.value)}
                     InputProps={{ startAdornment: (
                       <InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment>
                     )}}
                     sx={{ flex: 1, minWidth: 200 }} />
          <Button variant="text" onClick={() => { setFrom(yearStartISO()); setTo(todayISO()); }}>
            YTD
          </Button>
        </Stack>
      </Paper>

      {/* By-type summary chips */}
      {data.by_type.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography sx={{ fontSize: 12, color: "text.secondary", mr: 1 }}>
              Total: {money(data.total)} ({data.count} entries)
            </Typography>
            {data.by_type.map((b) => (
              <Chip key={b.type} size="small" variant="outlined"
                    label={`${b.type}: ${moneyDelta(b.amount)}`}
                    sx={{ height: 22, fontVariantNumeric: "tabular-nums" }} />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Table */}
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 130 }}>Date</TableCell>
                <TableCell sx={{ width: 200 }}>Type</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Amount</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell sx={{ width: 110 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && data.rows.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={20} />
                </TableCell></TableRow>
              )}
              {!loading && data.rows.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  No expenses in this window.
                </TableCell></TableRow>
              )}
              {data.rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{row.date_iso}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={row.type}
                          sx={{ height: 22 }} />
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontVariantNumeric: "tabular-nums", fontWeight: 500,
                    color: row.amount < 0 ? "success.main" : "text.primary",
                  }}>
                    {moneyDelta(row.amount)}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", whiteSpace: "normal",
                                   maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.notes || "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" justifyContent="flex-end" spacing={0.25}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => deleteRow(row)}>
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / Edit dialog. Autocomplete on type lets the operator pick
          an existing category OR type a new one — same UX as the
          tag-style inputs on Sales / Subscriptions. */}
      <Dialog open={!!editing} onClose={closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>{editing?.id ? "Edit expense" : "Add expense"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="Date" type="date"
                       value={editing?.date || ""}
                       onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                       InputLabelProps={{ shrink: true }} fullWidth />
            <Autocomplete
              freeSolo
              options={allTypeOptions}
              value={editing?.type || ""}
              onChange={(_, v) => setEditing({ ...editing, type: v || "" })}
              onInputChange={(_, v) => setEditing((prev) => ({ ...(prev || {}), type: v || "" }))}
              renderInput={(params) => <TextField {...params} label="Type" fullWidth />}
            />
            <TextField label="Amount" type="number" fullWidth
                       value={editing?.amount ?? ""}
                       onChange={(e) => setEditing({ ...editing, amount: e.target.value })}
                       inputProps={{ step: "0.01" }}
                       helperText="Use a negative number for credits / refunds"
                       InputProps={{ startAdornment: (
                         <InputAdornment position="start">$</InputAdornment>
                       )}} />
            <TextField label="Notes" fullWidth multiline minRows={2}
                       value={editing?.notes ?? ""}
                       onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={savingDialog}>Cancel</Button>
          <Button variant="contained" onClick={saveDialog} disabled={savingDialog}
                  startIcon={savingDialog ? <CircularProgress size={14} color="inherit" /> : null}>
            {savingDialog ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
